import React, { useMemo, useState, useCallback } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import useDarkMode from '../../hooks/useDarkMode';
import './PhoneNumberInput.css';

/**
 * PhoneNumberInput
 * A wrapper around react-phone-input-2 that exposes a simple API and works with our forms.
 * Props:
 * - value: string (e.g., '+44 7700 900123')
 * - onChange: (value: string, country: { dialCode?: string; countryCode?: string }) => void
 * - defaultCountryIso: string (e.g., 'gb') to set initial country
 * - disabled: boolean
 * - inputProps: object passed to the underlying input
 */
const PhoneNumberInput = ({
	value,
	onChange,
	defaultCountryIso,
	disabled = false,
	inputProps = {},
}) => {
	const { darkModeStatus } = useDarkMode();
	const country = useMemo(
		() => (defaultCountryIso ? defaultCountryIso.toLowerCase() : undefined),
		[defaultCountryIso],
	);

	const [lastDialCode, setLastDialCode] = useState(undefined);

	const handleChange = useCallback(
		(val, ctry, e, formattedValue) => {
			if (ctry && ctry.dialCode) setLastDialCode(ctry.dialCode);
			if (onChange) onChange(val, ctry, e, formattedValue);
		},
		[onChange],
	);

	const isCodeSelected = useMemo(() => {
		const hasAnyValue = typeof value === 'string' && value.trim().length > 0;
		return Boolean(country) || Boolean(lastDialCode) || hasAnyValue;
	}, [country, lastDialCode, value]);

	const containerClass = `sd-phone-input${isCodeSelected ? '' : ' sd-phone-empty'}`;

	return (
		<PhoneInput
			country={country}
			value={value}
			onChange={handleChange}
			disabled={disabled}
			containerClass={containerClass}
			buttonClass='sd-phone-flag'
			dropdownClass='sd-phone-dropdown'
			searchClass='sd-phone-search sd-phone-search-box'
			inputClass={'form-control'}
			inputProps={{
				name: 'phone',
				required: false,
				autoFocus: false,
				...inputProps,
			}}
			enableSearch
			countryCodeEditable={false}
			placeholder={''}
			containerStyle={{ width: '100%' }}
			inputStyle={{
				width: '100%',
				background: darkModeStatus ? '#212529' : '#f8f9fa',
				color: darkModeStatus ? '#fff' : '#323232',
				borderRadius: '1rem',
				minHeight: '47.5px',
				border: darkModeStatus ? '1px solid #353b3fff' : '1px solid #ecececc9',
				boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.075)',
			}}
			buttonStyle={{
				borderTopLeftRadius: '1rem',
				borderBottomLeftRadius: '1rem',
				background: darkModeStatus ? '#212529' : '#f8f9fa',
				border: darkModeStatus ? '1px solid #353b3fff' : '1px solid #ecececc9',
			}}
			dropdownStyle={{
				background: darkModeStatus ? '#212529' : '#fff',
				color: darkModeStatus ? '#fff' : '#323232',
				borderRadius: '1rem',
				border: darkModeStatus ? '1px solid #5c5d5f' : '1px solid #ecececc9',
			}}
		/>
	);
};

export default PhoneNumberInput;
